import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_01: GgxTourDefinition = {
    key: '[HOWTO-01] What is a Pipeline',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 3,
    prerequisite: {
        target: '[test-id="home-page-card-Data & AI Assets"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'What is a Pipeline?',
        subtitle:
            'A pipeline is an end-to-end AI solution that connects all the moving parts, ' +
            'Agents, LLMs, Prompts, Knowledge Bases and Tool calls, into one cohesive system. ' +
            'In this tour we will walk through what that means for a Conversational AI Chatbot.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen how a pipeline is organized in GGX: its documentation, the Prompts, ' +
            'LLMs and RAGs inside it, the tests run against it, how risk is identified and ' +
            'mitigated, and the approval trail across its lifecycle. Next, explore risks in ' +
            'GenAI, or start building your own pipeline.',
        learned: [
            'A **pipeline** bundles prompts, LLMs and RAGs behind one use case.',
            'Its **tabs** hold the tests, risks and approvals run against it.',
            'Every change leaves an **approval trail** across its lifecycle.',
        ],
    },
    chapters: [
        {
            name: 'The concept',
            blurb: 'What a pipeline is, with a chatbot example',
            icon: 'grid',
            steps: [2],
        },
        {
            name: 'Find the pipeline',
            blurb: 'From the home page to an open pipeline',
            icon: 'map',
            steps: [3, 4, 5, 6],
        },
        {
            name: 'Inside the pipeline',
            blurb: 'Details, code and the AI assets it uses',
            icon: 'pulse',
            steps: [7, 8, 9, 10, 11],
        },
        {
            name: 'Activities & governance',
            blurb: 'Testing, evaluations, risk, approvals and actions',
            icon: 'compass',
            steps: [12, 13, 14, 15, 16, 17],
        },
    ],
    steps: [
        {
            title: 'So, what is a pipeline?',
            whatHappens: "we start on the home page and open the assistant's pipeline together.",
            icon: 'grid',
            description:
                'Imagine a customer service assistant for a credit card company. When a customer ' +
                'writes in, it works out what they want, looks up their account if it needs to, ' +
                'writes a reply, and hands anything unusual to a human. Behind that one ' +
                'assistant sit several parts: a language model, prompts that instruct it, a ' +
                'knowledge base to look things up in, and small functions that connect them. A ' +
                'pipeline is what ties those parts into one working assistant. This tour walks ' +
                'through a real one.',
        },
        {
            target: '[test-id="home-page-card-Data & AI Assets"] [test-id="launch-button"]',
            title: 'Every part of the assistant lives here',
            whatHappens: 'the Data & AI Assets module opens with its sections in the sidebar.',
            icon: 'map',
            description:
                '<strong>Data & AI Assets</strong> is the module that stores your building ' +
                'blocks: models, prompts, knowledge bases (RAGs), agents and the pipelines that ' +
                'combine them. Keeping them in one place is what makes testing and governance ' +
                'possible later. Click <strong>Launch</strong> on this card.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="sidenav-item-tab-Agents"]',
            title: 'Two small pipelines make one assistant',
            whatHappens: 'the registry opens, listing the pipelines in groups.',
            icon: 'map',
            description:
                'Our assistant answers in two steps. First it works out the intent behind the ' +
                'question, then it writes a response. Each step is a small pipeline of its own, ' +
                'and a third pipeline chains them together. All three are listed on the ' +
                '<strong>Agents</strong> page, the catalogue of every pipeline in the ' +
                'project. Click it in the sidebar.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Agents',
        },
        {
            target: '[test-id="group-Conversational AI Chatbot Pipeline (custom-built)"]',
            climbToRow: true,
            advanceWhenPresent:
                '[test-id="grid-row-link-Combined Customer Intent and Response Pipeline"]',
            title: 'Pipelines are grouped by use case',
            whatHappens: 'the group opens to show the three pipelines inside it.',
            icon: 'grid',
            description:
                'When a project has many pipelines, groups keep the related ones together. ' +
                'Everything for our assistant sits in <strong>Conversational AI Chatbot Pipeline ' +
                '(custom-built)</strong>. Click the group to expand it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'the group',
        },
        {
            target: '[test-id="grid-row-link-Combined Customer Intent and Response Pipeline"]',
            climbToRow: true,
            scrollIntoView: true,
            title: "Let's open the one that ties it all together",
            whatHappens: "the pipeline's page opens on its description.",
            icon: 'map',
            description:
                'Inside the group are the intent pipeline, the response pipeline and the ' +
                'combined one that runs them in order. The combined pipeline is the assistant as ' +
                'customers experience it, so that is the one we will look at. Click ' +
                '<strong>Combined Customer Intent and Response Pipeline</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            target: '[test-id="description"]',
            frame: true,
            scrollIntoView: true,
            dockCard: 'beside',
            title: 'First, what it is for',
            whatHappens: 'we move down to the details that classify the pipeline.',
            icon: 'grid',
            description:
                'Every object in GGX starts with a description, and this one explains what the ' +
                'assistant does, how it works at a high level and what it is allowed to handle. ' +
                'When a reviewer or a new teammate opens the pipeline, this is what they read ' +
                'first, so it is worth keeping accurate. Have a look, then click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="task-type"]',
            scrollIntoView: true,
            title: 'How the pipeline is classified',
            whatHappens: 'next we look under the hood in the Code tab.',
            icon: 'list',
            description:
                'The Details section captures the facts that govern the pipeline. ' +
                '<strong>Usecase Type</strong> says what it is for, such as answering questions ' +
                'or summarizing. <strong>Task Type</strong> says how it does it, for example ' +
                'generating free text or picking from a set of replies. Fields like Data Usage ' +
                'and Permissible Purpose record what data it may touch and why, which is what ' +
                'risk and compliance teams look for. Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Code"]',
            scrollIntoView: true,
            title: "Let's look under the hood",
            whatHappens: "the Code tab opens with the pipeline's settings, resources and logic.",
            icon: 'compass',
            description:
                'The <strong>Code</strong> tab is where the pipeline is actually built. It ' +
                'defines what input the pipeline expects, which models, prompts and other assets ' +
                'it uses, and the logic that connects them, written in Python or as a connection ' +
                'to an external agent. Click <strong>Code</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'the Code tab',
        },
        {
            target: '[test-id="input-type"]',
            scrollIntoView: true,
            title: 'Built in GGX, or connected from outside?',
            whatHappens: 'next we look at the resources this pipeline uses.',
            icon: 'grid',
            description:
                'A pipeline is one of two kinds. <strong>Custom Built</strong> means every part ' +
                'is registered in GGX and the logic joining them is written here, so you can see ' +
                'and test all of it. <strong>External Agent</strong> means the assistant was ' +
                'built somewhere else and GGX connects to it through an API. Our assistant is ' +
                'custom built. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="section-Resources"]',
            frame: true,
            scrollIntoView: true,
            title: 'Everything the pipeline relies on',
            whatHappens:
                'we head back up to the tabs to see how the pipeline is tested and governed.',
            icon: 'pulse',
            description:
                'The Resources section lists every asset the pipeline calls: its language ' +
                'models, prompts, knowledge bases, sub-pipelines, agents and tool calls. Think ' +
                "of it as the pipeline's bill of materials. If a prompt changes or a model is " +
                'retired, this is how you know which pipelines are affected. Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Details"]',
            scrollIntoView: true,
            title: 'Building is only half the story',
            whatHappens: 'we look at Chats first.',
            icon: 'compass',
            description:
                'Once a pipeline exists, everything else about it lives in these tabs: how it is ' +
                "tested, what risks it carries, who approved it. Let's walk through the ones " +
                'that matter most. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Chats"]',
            scrollIntoView: true,
            title: 'Chats: try it like a customer would',
            whatHappens: 'next is Jobs, where testing happens at scale.',
            icon: 'pulse',
            description:
                'The <strong>Chats</strong> tab lets you talk to the pipeline as if you were a ' +
                'customer, see how it replies and give feedback on each answer. It is the ' +
                'quickest way to get a feel for how the assistant behaves and to catch obvious ' +
                'problems early. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Jobs"]',
            scrollIntoView: true,
            title: 'Jobs: test it at scale',
            whatHappens: 'next, the Risk Assessment tab.',
            icon: 'list',
            description:
                'Chatting tests one question at a time. <strong>Jobs</strong> is where you send ' +
                'hundreds of questions through the pipeline and score the answers automatically, ' +
                'using rules, language analysis or another model acting as a judge. Those scores ' +
                'are how you measure risk with numbers rather than impressions. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Risk Assessment"]',
            scrollIntoView: true,
            title: 'Risk: identify, measure, mitigate',
            whatHappens: 'next, the Approvals tab.',
            icon: 'search',
            description:
                'GGX handles risk in three steps: identify what could go wrong, measure how ' +
                'often it does, and put controls in place. The <strong>Risk Assessment</strong> ' +
                'tab is where each risk for this pipeline is written down along with how it is ' +
                'measured and mitigated, so nothing is left to memory. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Approvals"]',
            scrollIntoView: true,
            title: 'Approvals: the paper trail',
            whatHappens: 'last, a look at the action buttons.',
            icon: 'map',
            description:
                'Before a pipeline reaches customers, the right people need to review and sign ' +
                'off. The <strong>Approvals</strong> tab records those reviews and decisions. ' +
                'Once approved, the pipeline is locked so it cannot change quietly, and it can ' +
                'be released to production through your deployment integrations. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="pipeline-action-buttons"]',
            title: 'And these are how you work on it',
            whatHappens: 'the tour wraps up with a summary of what you have seen.',
            icon: 'compass',
            description:
                'These buttons cover the everyday actions. <strong>Edit</strong> opens the ' +
                'pipeline for changes. <strong>Run</strong> tests it, either in a chat or as a ' +
                'simulation on a dataset. <strong>Copy</strong> makes your own version to ' +
                'experiment with while the original stays untouched. The <strong>More</strong> ' +
                'menu holds the rest: export the pipeline as Python code, move it to another ' +
                'project, archive it or delete it. Click Continue to finish.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'next'],
        },
    ],
};
